import React from 'react';
import {
    Stepper,
    Step,
    StepConnector,
    stepConnectorClasses,
    Box,
    Typography,
    StepLabel,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import CheckIcon from '@mui/icons-material/Check';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';

const steps = [
    'Personal Information',
    'Address & Services',
    'Supporting Documents',
    'Declaration & Review',
];

type Props = {
    activeStep?: number;
};

// ✅ Static connector (default gray — orange is applied per step below)
const OrangeConnector = styled(StepConnector)(({ theme }) => ({
    [`&.${stepConnectorClasses.alternativeLabel}`]: {
        top: 14,
        [theme.breakpoints.down('sm')]: {
            top: 12,
        },
    },
    [`& .${stepConnectorClasses.line}`]: {
        borderTopWidth: 3,
        borderRadius: 1,
        borderColor: '#E0E0E0',
        [theme.breakpoints.down('sm')]: {
            borderTopWidth: 1.5,
        },
    },
}));

// ✅ Custom Step Icon
const StepIconRoot = styled('div')<{
    ownerState: { active: boolean; completed: boolean };
}>(({ ownerState, theme }) => ({
    width: 32,
    height: 32,
    borderRadius: '50%',
    backgroundColor: ownerState.completed || ownerState.active ? '#FF544B' : '#E0E0E0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#fff',
    fontWeight: 600,
    fontSize: 16,
    [theme.breakpoints.down('sm')]: {
        width: 24,
        height: 24,
        fontSize: 12,
    },
}));

const CustomStepIcon = ({ active, completed, icon }: any) => {
    return (
        <StepIconRoot ownerState={{ active, completed }}>
            {completed ? (
                <CheckIcon fontSize='small' />
            ) : (
                <FiberManualRecordIcon fontSize='small' />
            )}
        </StepIconRoot>
    );
};

// Gensize Stepper
const GensizeStepper = ({ activeStep = 0 }: Props) => {
    return (
        <Box
            sx={{
                width: '100%',
                maxWidth: { xs: '100%', sm: 500, md: 500 },
                mx: 'auto',
                overflow: 'hidden',
            }}
        >
            <Stepper 
                activeStep={activeStep} 
                alternativeLabel 
                connector={<OrangeConnector />}
                sx={{
                    '& .MuiStepLabel-root': {
                        flexDirection: 'column',
                        alignItems: 'center',
                    },
                    '& .MuiStepLabel-label': {
                        fontSize: { xs: '11px', sm: '14px', md: '15px' },
                        lineHeight: { xs: '1.2', sm: '1.4' },
                        textAlign: 'center',
                        wordBreak: 'break-word',
                        maxWidth: { xs: '80px', sm: '140px', md: '160px' },
                    },
                    '& .MuiStepConnector-root': {
                        top: { xs: 12, sm: 16 },
                    },
                    '& .MuiStepConnector-line': {
                        borderTopWidth: { xs: 1.5, sm: 3 },
                    },
                }}
            >
                {steps.map((label, index) => (
                    <Step
                        key={label}
                        completed={index < activeStep}
                        sx={{
                            [`& .${stepConnectorClasses.line}`]: {
                                borderColor: index < activeStep ? '#FF544B' : '#E0E0E0',
                            },
                            '& .MuiStepLabel-root': {
                                minHeight: { xs: '60px', sm: 'auto' },
                            },
                        }}
                    >
                        <StepLabel StepIconComponent={CustomStepIcon}>
                            <Typography
                                variant='caption'
                                sx={{
                                    mt: { xs: 0.5, sm: 1.5 },
                                    fontWeight: 600,
                                    fontSize: { xs: 11, sm: 14, md: 15 },
                                    textAlign: 'center',
                                    color: activeStep === index ? '#FF544B' : '#6B7280',
                                    lineHeight: { xs: '1.2', sm: '1.4' },
                                    wordBreak: 'break-word',
                                    maxWidth: { xs: '80px', sm: '140px', md: '160px' },
                                }}
                            >
                                {label}
                            </Typography>
                        </StepLabel>
                    </Step>
                ))}
            </Stepper>
        </Box>
    );
};

export default GensizeStepper;
